import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Menu, X, Phone, Settings } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { mediaUrl, waLink } from "@/lib/api";
import { scrollToId } from "@/lib/lenis";

const LINKS = [
  ["servicios", "nav.services"],
  ["galeria", "nav.gallery"],
  ["equipo", "nav.team"],
  ["opiniones", "nav.reviews"],
  ["horario", "nav.hours"],
  ["cita", "nav.book"],
];

export const Header = () => {
  const { t, lang, toggle } = useI18n();
  const { data } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const s = data?.settings || {};

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(`#${id}`);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-ink/85 backdrop-blur-md border-b border-gold/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <button data-testid="header-logo-button" onClick={() => go("inicio")} className="flex items-center gap-3">
            {s.hero_logo_path && (
              <img src={mediaUrl(s.hero_logo_path)} alt="Blackbeard Barbería" className="h-10 w-auto" />
            )}
            <span className="font-display text-xs sm:text-sm tracking-[0.18em] text-bone hidden sm:block">
              {s.business_name || "BLACKBEARD BARBERÍA"}
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map(([id, key]) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => go(id)}
                className="text-[12px] uppercase tracking-[0.16em] text-mut hover:text-gold transition-colors whitespace-nowrap"
              >
                {t(key)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <button
              data-testid="language-toggle-button"
              onClick={toggle}
              className="font-mono text-xs border border-gold/30 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors"
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
            <a
              data-testid="contact-phone-link"
              href={`tel:${(s.phone || "").replace(/\D/g, "")}`}
              className="hidden md:flex items-center gap-2 font-mono text-xs text-mut hover:text-gold transition-colors whitespace-nowrap"
            >
              <Phone size={13} />
              {s.phone}
            </a>
            <a
              data-testid="whatsapp-cta-button"
              href={waLink(s.whatsapp)}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 bg-gold text-ink font-semibold text-sm px-4 py-2 rounded-full hover:bg-goldlight transition-colors whitespace-nowrap"
            >
              <MessageCircle size={15} />
              {t("nav.book")}
            </a>
            <a
              data-testid="header-admin-link"
              href="/admin"
              title={t("footer.admin_link")}
              className="text-mut/60 hover:text-gold transition-colors p-2"
            >
              <Settings size={17} />
            </a>
            <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} className="lg:hidden text-bone p-2">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 top-16 z-40 bg-ink/[0.98] backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-8 pt-10 gap-2">
              {LINKS.map(([id, key], i) => (
                <motion.button
                  key={id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => go(id)}
                  className="text-left font-display text-3xl text-bone py-3 border-b border-white/5 hover:text-gold transition-colors"
                >
                  {t(key)}
                </motion.button>
              ))}
              <a
                data-testid="mobile-whatsapp-cta-button"
                href={waLink(s.whatsapp)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center justify-center gap-2.5 bg-gold text-ink font-semibold px-7 py-3.5 rounded-full"
              >
                <MessageCircle size={17} />
                {t("hero.cta_whatsapp")}
              </a>
              <a
                data-testid="mobile-phone-link"
                href={`tel:${(s.phone || "").replace(/\D/g, "")}`}
                className="mt-3 text-center font-mono text-sm text-mut"
              >
                {s.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
